import { useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@rudi/ui/table';
import { Badge } from '@rudi/ui/badge';
import { Button } from '@rudi/ui/button';
import { Input } from '@rudi/ui/input';
import { Label } from '@rudi/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@rudi/ui/dialog';
import { toast } from 'sonner';
import { PageHeader, QueryState } from '../components/page';
import { useDeleteGlobalKey, useGlobalKeys, useUpsertGlobalKey } from '../api/hooks';
import { SpeechCredentials } from './SpeechCredentials';
import { DictationSettings } from './DictationSettings';

function UpsertKeyDialog({
  onClose,
  initialProvider,
}: {
  onClose: () => void;
  initialProvider: string;
}) {
  const [provider, setProvider] = useState(initialProvider);
  const [apiKey, setApiKey] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const upsert = useUpsertGlobalKey();

  const save = async () => {
    try {
      await upsert.mutateAsync({
        provider: provider.trim(),
        api_key: apiKey.trim(),
        display_name: displayName.trim() || null,
        base_url: baseUrl.trim() || null,
      });
      toast.success('Global key saved');
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Save failed');
    }
  };

  return (
    <Dialog open onOpenChange={open => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Set global provider key</DialogTitle>
          <DialogDescription>
            Platform-wide fallback key for an AI provider. Tenants without their own key use this.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="provider-key-provider">Provider</Label>
            <Input
              id="provider-key-provider"
              value={provider}
              onChange={e => setProvider(e.target.value)}
              placeholder="openrouter, infercom, openai, anthropic"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="provider-key-secret">API key</Label>
            <Input
              id="provider-key-secret"
              type="password"
              value={apiKey}
              onChange={e => setApiKey(e.target.value)}
              placeholder="sk-…"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="provider-key-name">Display name (optional)</Label>
            <Input
              id="provider-key-name"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="provider-key-base-url">Base URL (optional)</Label>
            <Input
              id="provider-key-base-url"
              value={baseUrl}
              onChange={e => setBaseUrl(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save} disabled={upsert.isPending || !provider.trim() || !apiKey.trim()}>
            {upsert.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ApiKeysPage() {
  const { data, isLoading, error } = useGlobalKeys();
  const del = useDeleteGlobalKey();
  const [adding, setAdding] = useState(false);
  const [initialProvider, setInitialProvider] = useState('');

  const onDelete = async (provider: string) => {
    if (!confirm(`Delete the global ${provider} key?`)) return;
    try {
      await del.mutateAsync(provider);
      toast.success('Key deleted');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Delete failed');
    }
  };

  return (
    <div>
      <PageHeader
        title="Providers and credentials"
        description="Speech credential availability and Rudi global provider keys."
        actions={
          <Button
            onClick={() => {
              setInitialProvider('');
              setAdding(true);
            }}
          >
            Set key
          </Button>
        }
      />
      <SpeechCredentials />
      <h2 className="mb-3 text-lg font-semibold">Rudi global provider keys</h2>
      <QueryState isLoading={isLoading} error={error} empty={data?.keys.length === 0}>
        <div className="rounded-3xl border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Provider</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Identifier</TableHead>
                <TableHead>Status</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.keys.map(k => (
                <TableRow key={k.id}>
                  <TableCell className="font-medium">{k.provider}</TableCell>
                  <TableCell className="text-muted-foreground">{k.displayName ?? '—'}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {k.identifier ?? '—'}
                  </TableCell>
                  <TableCell>
                    <Badge variant={k.isActive ? 'default' : 'secondary'}>
                      {k.isActive ? 'active' : 'inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => onDelete(k.provider)}>
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </QueryState>

      <DictationSettings
        onSetKey={provider => {
          setInitialProvider(provider);
          setAdding(true);
        }}
      />
      {adding && (
        <UpsertKeyDialog initialProvider={initialProvider} onClose={() => setAdding(false)} />
      )}
    </div>
  );
}
